import * as React from "react";
import getConfig from "next/config";
import { Store } from "@grandvisionhq/analytics";
import { useBloomreachPageRoutes, useBloomreachSdpContent, CmsHeadTags } from "@grandvisionhq/bloomreach";
import { Container } from "@grandvisionhq/elements";
import { useIntl, useFeatureToggles } from "@grandvisionhq/state";
import { Containers, Util, StoreDetailsRoot, DetailsPage } from "@grandvisionhq/stores-v2";
import { useTealiumEventProperties, useTrackPageView } from "@grandvisionhq/svc-tealium/events";
import { slugify, EnvironmentUtils } from "@grandvisionhq/utils";
const { publicRuntimeConfig } = getConfig();
const selfUrl = publicRuntimeConfig?.selfUrl || "";
const siteName = publicRuntimeConfig?.site.name || "";
const opcoDefaultAppointmentTypeId = publicRuntimeConfig?.opco?.defaultAppointmentTypeId || "";
const PreferredStoreButton = Containers.PreferredStoreButton;
const Page = ({
  pageTitle,
  storeData,
  nearestStores,
  urls: overwriteUrls,
  dropInAppointmentBookingWidget
}) => {
  const { getLabel } = useIntl();
  const features = useFeatureToggles();
  const { routes } = useBloomreachPageRoutes();
  const {
    storeFallbackImage,
    offerFallbackImage,
    productCategoryFallbackImage,
    paymentIcons,
    socialMediaIcons,
    campaignBanner,
    productCategories
  } = useBloomreachSdpContent();
  const { commonEventProperties, getCommonUserProperties } = useTealiumEventProperties();
  const urls = React.useMemo(
    () => ({
      bookAppointment: `${routes["appointment-book"]}/what`,
      storeDetails: routes.stores,
      storeOverview: routes["store-overview"],
      storeFallbackImage,
      offerFallbackImage,
      productCategoryFallbackImage,
      ...overwriteUrls
    }),
    [routes, storeFallbackImage, offerFallbackImage, productCategoryFallbackImage, overwriteUrls]
  );
  const logos = React.useMemo(
    () => ({
      paymentMethods: paymentIcons,
      socialMedia: socialMediaIcons
    }),
    [paymentIcons, socialMediaIcons]
  );
  const storeDetailsPageView = React.useCallback(
    async () => {
      const commonUserProperties = await getCommonUserProperties();
      if (!commonUserProperties) return;
      return {
        id: "VirtualPage-View",
        Page_Type: "Static",
        Page_Section1: "Locator",
        Page_Section2: pageTitle ?? storeData?.name ?? "",
        ...commonEventProperties,
        ...commonUserProperties
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCommonUserProperties]
  );
  useTrackPageView(storeDetailsPageView);
  if (!storeData) {
    console.error(
      `Tried to render a store detail page but got invalid store data: ${String(storeData)}`
    );
    return null;
  }
  const pageUrl = `${selfUrl}${routes.stores}/${slugify(storeData.town)}/${storeData.slug}`;
  const breadcrumbs = Util.createBreadcrumbs(
    storeData,
    getLabel("stores.form.findStore.title"),
    routes.stores
  );
  const defaultAppointmentTypeId = Boolean(storeData.defaultAppointmentTypeId) ? storeData.defaultAppointmentTypeId : opcoDefaultAppointmentTypeId;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    CmsHeadTags,
    {
      title: pageTitle ?? storeData.name,
      description: storeData.metaDescription,
      ogUrl: pageUrl
    }
  ), /* @__PURE__ */ React.createElement(Container, { className: "stores-v2-layout" }, /* @__PURE__ */ React.createElement(
    StoreDetailsRoot,
    {
      store: storeData,
      campaignBanner,
      productCategories,
      nearestStores,
      hidePhone: !features.isStoreDetailsPhoneEnabled,
      hideEmail: !features.isStoreDetailsEmailEnabled,
      opco: siteName,
      breadcrumbs,
      urls,
      appointmentTypeId: defaultAppointmentTypeId,
      logos,
      dropInAppointmentBookingWidget,
      preferredStoreButton: /* @__PURE__ */ React.createElement(
        PreferredStoreButton,
        {
          storeId: storeData.globalStoreId,
          storeName: storeData.shortName ?? storeData.name
        }
      )
    }
  )), /* @__PURE__ */ React.createElement(
    Store,
    {
      url: `${selfUrl}${routes.stores}`,
      store: {
        ...storeData,
        storeName: storeData.name,
        storeEmailAddress: storeData.emailAddress,
        Phone1: storeData.phone
      }
    }
  ));
};
Page.getInitialProps = async (ctx) => {
  const { asPath = "", apolloClient, req, pathname, featureToggles } = ctx;
  const cleanPath = asPath.split("?")[0];
  const slug = cleanPath && [...cleanPath.split("/")].pop();
  const locale = req?.locale?.toString();
  const features = EnvironmentUtils.getFeatures();
  const withNearbyStores = features.includes("STORES_NEARBY");
  const {
    featureConfigs: { maxNearbyStoresDistance }
  } = featureToggles;
  if (!apolloClient) {
    console.error(`ApolloClient not present in context for page ${pathname}`);
    return {
      statusCode: 500
    };
  }
  if (!slug) {
    console.error(`Can't extract slug from ${asPath}: '${String(slug)}'`);
    return {
      statusCode: 404
    };
  }
  try {
    const data = await DetailsPage.getPageData(
      apolloClient,
      slug,
      locale,
      withNearbyStores,
      maxNearbyStoresDistance
    );
    if (!data.storeData) {
      return {
        statusCode: 404
      };
    }
    return data;
  } catch (err) {
    console.error(`Failed to fetch Store with slug: ${String(slug)}.`, err);
    return {
      statusCode: 500
    };
  }
};
export {
  Page
};
//# sourceMappingURL=store-detail.js.map
